import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, Image, StyleSheet } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { getConditionDisplayText, ListingCondition } from '@/types/residents/marketplaceListing';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

interface ListingRowCardProps {
  imageUri?: string;
  price: string;
  title: string;
  condition: ListingCondition;
  onPress: () => void;
  isSold?: boolean;
  rightElement?: React.ReactNode;
}

/**
 * Base card component for horizontal listing display (My Listings, Saved Listings)
 */
export const ListingRowCard: React.FC<ListingRowCardProps> = ({
  imageUri,
  price,
  title,
  condition,
  onPress,
  isSold = false,
  rightElement,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: { backgroundColor: mc.cardBackground, borderRadius: 16, padding: 16, flexDirection: 'row', gap: 16 },
    imageContainer: { width: 85, height: 85, borderRadius: 14, borderWidth: 0.45, borderColor: mc.borderGray, overflow: 'hidden' },
    image: { width: '100%', height: '100%' },
    placeholderContainer: { width: '100%', height: '100%', backgroundColor: mc.uploadAreaBackground, alignItems: 'center', justifyContent: 'center' },
    content: { flex: 1, gap: 2 },
    priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    priceContainer: { flexDirection: 'row', alignItems: 'center', flex: 1 },
    price: { fontSize: 14, fontWeight: '600', color: mc.textPrimary, lineHeight: 21 },
    priceDot: { fontSize: 14, fontWeight: '600', color: mc.textPrimary, lineHeight: 21 },
    soldText: { fontSize: 14, fontWeight: '500', color: mc.dangerRed, lineHeight: 21 },
    title: { fontSize: 14, fontWeight: '400', color: mc.textPrimary, lineHeight: 21 },
    condition: { fontSize: 12, fontWeight: '400', color: mc.textSecondary, lineHeight: 18 },
  }), [mc]);

  return (
    <TouchableOpacity style={styles.container} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.imageContainer}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.image} resizeMode="cover" />
        ) : (
          <View style={styles.placeholderContainer}>
            <PropertyPlaceholder width={65} height={65} />
          </View>
        )}
      </View>
      <View style={styles.content}>
        <View style={styles.priceRow}>
          <View style={styles.priceContainer}>
            <Text style={styles.price}>{price}</Text>
            {isSold && (
              <>
                <Text style={styles.priceDot}> • </Text>
                <Text style={styles.soldText}>Sold</Text>
              </>
            )}
          </View>
          {rightElement}
        </View>
        <Text style={styles.title} numberOfLines={2}>{title}</Text>
        <Text style={styles.condition}>{getConditionDisplayText(condition)}</Text>
      </View>
    </TouchableOpacity>
  );
};
